// src/components/setup-checklist/SetupChecklistWidget.js
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { fetchSetupChecklist, dismissChecklistItem, restoreChecklistItem, remindLaterChecklist } from 'src/redux/thunks/setupChecklistThunks';

// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';

// icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SnoozeIcon from '@mui/icons-material/Snooze';

import { paths } from 'src/routes/paths';

// ────────────────────────────────────────────────────────────────
// Map each checklist key → the tab value in TenantConfigurationForm
// ────────────────────────────────────────────────────────────────
const CHECKLIST_TAB_MAP = {
  JOB_PREFIX: 'jobCodePrefix',
  EMPLOYEE_CODE: 'employee',
  LINKEDIN: 'linkedin',
  ZOOM: 'zoom',
  MICROSOFT_TEAMS: 'teams',
  // GOOGLE_MEET: 'googleMeet',
};

const REMIND_LATER_HOURS = 24;

// Gradient tokens (shared between sidebar & dashboard header)
const GRADIENT_LIGHT = 'linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)';
const GRADIENT_DARK = 'linear-gradient(135deg, #1a237e 0%, #283593 100%)';

const getProgressColor = (progressVal) => {
  if (progressVal <= 34) return '#FF5630'; // Red
  if (progressVal <= 66) return '#FFCA28'; // Yellow
  return '#22C55E'; // Green
};

const getProgressTextColor = (progressVal) => {
  if (progressVal > 34 && progressVal <= 66) return '#1C252E'; // Black text for yellow
  return '#FFFFFF'; // White text
};

// ────────────────────────────────────────────────────────────────
// Helper: check if current user has "Super Admin" role
// ────────────────────────────────────────────────────────────────
function useIsSuperAdmin() {
  const sites = useSelector((state) => state.auth.sites);

  return useMemo(() => {
    try {
      const roles = Array.isArray(sites) ? sites : [sites];
      return roles.some((r) => {
        const name = (r?.roleName || '').toLowerCase();
        return name.includes('super admin') || name.includes('superadmin');
      });
    } catch {
      return false;
    }
  }, [sites]);
}

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// variant: "sidebar" (compact nav row) | "dashboard" (full page view)
// ════════════════════════════════════════════════════════════════
export default function SetupChecklistWidget({ variant = 'sidebar' }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    overallComplete, completedCount, totalCount, items,
    dismissedKeys, initialized, loading, remindLaterUntil,
  } = useSelector((state) => state.setupChecklist);

  const isSuperAdmin = useIsSuperAdmin();

  // ── Auto-fetch if not initialized ──
  useEffect(() => {
    if (isSuperAdmin && !initialized && !loading) {
      dispatch(fetchSetupChecklist());
    }
  }, [isSuperAdmin, initialized, loading, dispatch]);

  const isDashboard = variant === 'dashboard';

  // ── Filter out GOOGLE_MEET defensively and compute stats ──
  const visibleItems = useMemo(
    () => (items || []).filter((item) => item.key !== 'GOOGLE_MEET'),
    [items]
  );
  const activeCompletedCount = useMemo(
    () => visibleItems.filter((item) => item.complete).length,
    [visibleItems]
  );
  const activeTotalCount = visibleItems.length;
  const isAllComplete = activeTotalCount > 0 && activeCompletedCount === activeTotalCount;

  // ── "Remind Me Later" check (sidebar only) ──
  const isReminded = useMemo(() => {
    if (!remindLaterUntil) return false;
    return Date.now() < remindLaterUntil;
  }, [remindLaterUntil]);

  // ── Gate: only show for Super Admin ──
  if (!isSuperAdmin) return null;
  if (!initialized) return null;
  if (loading && !initialized) return null;

  // Hide if reminded
  if (isReminded) return null;
  // Hide if all complete
  // if (overallComplete && completedCount === totalCount) return null;
  if (isAllComplete) return null;
  // Hide if no items
  // if (!items || items.length === 0) return null;
  if (!visibleItems || visibleItems.length === 0) return null;

  // const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const progress = activeTotalCount > 0 ? (activeCompletedCount / activeTotalCount) * 100 : 0;

  // ──────────────────────────────────────────────────────────────
  // SIDEBAR VARIANT — compact, clickable row, no dropdown
  // ──────────────────────────────────────────────────────────────
  if (!isDashboard) {
    return (
      <Card
        onClick={() => navigate(paths.dashboard.root)}
        sx={{
          mx: 2,
          mb: 1.5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: (theme) =>
            `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'}`,
          '&:hover': {
            boxShadow: (theme) =>
              `0 4px 16px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}`,
            transform: 'translateY(-1px)',
          },
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            background: (theme) =>
              theme.palette.mode === 'dark' ? GRADIENT_DARK : GRADIENT_LIGHT,
          }}
        >
          <RocketLaunchIcon
            sx={{
              mr: 1.25,
              fontSize: 20,
              color: (theme) => (theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'),
            }}
          />

          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography noWrap variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.8rem' }}>
              Setup Pending
            </Typography>
            <Typography noWrap variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
              {/* {completedCount} of {totalCount} completed */}
              {activeCompletedCount} of {activeTotalCount} completed
            </Typography>
          </Box>

          <Chip
            label={`${Math.round(progress)}%`}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 22,
              mr: 0.5,
              bgcolor: getProgressColor(progress),
              color: getProgressTextColor(progress),
              '& .MuiChip-label': {
                color: getProgressTextColor(progress),
              },
            }}
          />

          <ArrowForwardIosIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        </Box>

        {/* Thin progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            '& .MuiLinearProgress-bar': {
              bgcolor: getProgressColor(progress),
              transition: 'width 0.6s ease',
            },
          }}
        />
      </Card>
    );
  }

  // ──────────────────────────────────────────────────────────────
  // DASHBOARD VARIANT — full setup page with items + dismiss
  // ──────────────────────────────────────────────────────────────
  // return <DashboardChecklist
  //   items={items}
  //   dismissedKeys={dismissedKeys}
  //   completedCount={completedCount}
  //   totalCount={totalCount}
  //   progress={progress}
  //   dispatch={dispatch}
  //   navigate={navigate}
  // />;
  return <DashboardChecklist
    items={visibleItems}
    dismissedKeys={dismissedKeys}
    completedCount={activeCompletedCount}
    totalCount={activeTotalCount}
    progress={progress}
    dispatch={dispatch}
    navigate={navigate}
  />;
}

SetupChecklistWidget.propTypes = {
  variant: PropTypes.oneOf(['sidebar', 'dashboard']),
};

// ════════════════════════════════════════════════════════════════
// Dashboard sub-component (extracted to manage its own state)
// ════════════════════════════════════════════════════════════════
function DashboardChecklist({ items, dismissedKeys, completedCount, totalCount, progress, dispatch, navigate }) {
  const [dismissTarget, setDismissTarget] = useState(null); // key to confirm-dismiss
  const [showDismissed, setShowDismissed] = useState(false);

  const filteredDismissedKeys = useMemo(
    () => (dismissedKeys || []).filter((k) => k !== 'GOOGLE_MEET'),
    [dismissedKeys]
  );

  // ── "Remind Me Later" handler ──
  const handleRemindLater = useCallback(() => {
    dispatch(remindLaterChecklist());
  }, [dispatch]);

  // ── Dismiss confirm ──
  const handleConfirmDismiss = useCallback(() => {
    if (dismissTarget) {
      dispatch(dismissChecklistItem(dismissTarget));
      setDismissTarget(null);
    }
  }, [dispatch, dismissTarget]);

  // ── Restore ──
  const handleRestore = useCallback((key) => {
    dispatch(restoreChecklistItem(key));
  }, [dispatch]);

  return (
    <>
      <Card
        sx={{
          overflow: 'hidden',
          overflowX: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: (theme) =>
            `0 2px 12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'}`,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            background: (theme) =>
              theme.palette.mode === 'dark' ? GRADIENT_DARK : GRADIENT_LIGHT,
          }}
        >
          <RocketLaunchIcon
            sx={{
              mr: 1.5,
              fontSize: 26,
              color: (theme) => (theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'),
            }}
          />

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              Setup Pending
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {completedCount} of {totalCount} completed
            </Typography>
          </Box>

          <Chip
            label={`${Math.round(progress)}%`}
            size="small"
            sx={{
              fontWeight: 700,
              mr: 1.5,
              bgcolor: getProgressColor(progress),
              color: getProgressTextColor(progress),
              '& .MuiChip-label': {
                color: getProgressTextColor(progress),
              },
            }}
          />

          <Tooltip title="Remind me in 24 hours">
            <Button
              size="small"
              variant="text"
              startIcon={<SnoozeIcon sx={{ fontSize: '16px !important' }} />}
              onClick={handleRemindLater}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: (theme) => (theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'),
                borderRadius: 1.5,
                px: 1.5,
                '&:hover': { bgcolor: 'rgba(21, 101, 192, 0.08)' },
              }}
            >
              Remind Later
            </Button>
          </Tooltip>
        </Box>

        {/* ── Progress bar ── */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            '& .MuiLinearProgress-bar': {
              bgcolor: getProgressColor(progress),
              transition: 'width 0.6s ease',
            },
          }}
        />

        {/* ── Items grid ── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
          }}
        >
          {items.map((item, index) => {
            const isLastRow = index >= items.length - (items.length % 2 === 0 ? 2 : 1);

            return (
              <Box
                key={item.key}
                sx={{
                  px: 3,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  opacity: item.complete ? 0.55 : 1,
                  transition: 'all 0.2s ease',
                  borderBottom: (theme) =>
                    !isLastRow ? `1px solid ${theme.palette.divider}` : 'none',
                  // borderRight: (theme) =>
                  //   index % 2 === 0 ? `1px solid ${theme.palette.divider}` : 'none',
                  borderRight: (theme) =>
                    index % 2 === 0 && index < items.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {/* Status icon & Text */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
                  {item.complete ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main', flexShrink: 0, mt: 0.25 }} />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 20, color: 'warning.main', flexShrink: 0, mt: 0.25 }}
                    />
                  )}

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: item.complete ? 400 : 600,
                        textDecoration: item.complete ? 'line-through' : 'none',
                        lineHeight: 1.3,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, mt: 0.25 }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Box>

                {/* Action buttons */}
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  {/* Setup / Review button */}
                  {CHECKLIST_TAB_MAP[item.key] && (
                    <Button
                      size="small"
                      variant={item.complete ? 'text' : 'outlined'}
                      color={item.complete ? 'inherit' : 'primary'}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() =>
                        navigate(
                          `${paths.dashboard.preferences.TenantConfig}?tab=${CHECKLIST_TAB_MAP[item.key]}`
                        )
                      }
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        px: 1.5,
                        py: 0.25,
                        minWidth: 'auto',
                        borderRadius: 1.5,
                      }}
                    >
                      {item.complete ? 'Review' : 'Setup'}
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>

        {/* ── Dismissed items section ── */}
        {/* {dismissedKeys.length > 0 && ( */}
        {filteredDismissedKeys.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 3, py: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowDismissed((p) => !p)}
                endIcon={showDismissed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                {/* {showDismissed ? 'Hide' : 'Show'} skipped steps ({dismissedKeys.length}) */}
                {showDismissed ? 'Hide' : 'Show'} skipped steps ({filteredDismissedKeys.length})
              </Button>

              <Collapse in={showDismissed}>
                <Stack spacing={1} sx={{ py: 1 }}>
                  {/* {dismissedKeys.map((key) => ( */}
                  {filteredDismissedKeys.map((key) => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {key.replace(/_/g, ' ')}
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<RestoreIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={() => handleRestore(key)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          px: 1,
                          borderRadius: 1,
                        }}
                      >
                        Restore
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          </>
        )}
      </Card>

      {/* ── Confirmation Dialog ── */}
      <Dialog
        open={!!dismissTarget}
        onClose={() => setDismissTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 0.5 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          Skip this setup step?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This will permanently hide{' '}
            <strong>{dismissTarget?.replace(/_/g, ' ')}</strong> from your setup checklist.
            You can restore it later from the &quot;Skipped steps&quot; section.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDismissTarget(null)}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDismiss}
            variant="contained"
            color="warning"
            sx={{ textTransform: 'none', borderRadius: 1.5 }}
          >
            Skip this step
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

DashboardChecklist.propTypes = {
  items: PropTypes.array,
  dismissedKeys: PropTypes.array,
  completedCount: PropTypes.number,
  totalCount: PropTypes.number,
  progress: PropTypes.number,
  dispatch: PropTypes.func,
  navigate: PropTypes.func,
};
